import type { Operation } from '@apollo/client';

import { ApolloLink, HttpLink, split } from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';

import { GRAPHQL_BASE_HEADERS, GRAPHQL_SSR_HEADERS } from './graphqlHeaders';

// Allow-listed operations that can use GET + APQ for edge caching
// This list should mirror @see graphql/src/utils/cacheHeaderAllowList.ts
const GET_APQ_OPS = new Set([
  'Navigation',
  'MetaInfoPaymentMethods',
  'Usps',
  'StyleVariants',
  'Countries',
  'SizeGuides',
  'SizeMeasurements',
  'Products',
  'Recommendations',
  'ProductDynamicData',
]);

/**
 * SHA-256 hash function using Web Crypto API
 */
async function sha256(data: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

/**
 * Determines if an operation should use GET (for APQ cached queries)
 */
function shouldUseGet(operation: Operation): boolean {
  const { operationName } = operation;
  const isAllowListed = Boolean(operationName && GET_APQ_OPS.has(operationName));

  // Check if this operation has a persisted query hash
  const extensions = operation.extensions || {};
  const hasPersistedQuery = Boolean(extensions.persistedQuery?.sha256Hash);

  return isAllowListed && hasPersistedQuery;
}

/**
 * Creates the GET link for APQ cached queries
 * - Omits credentials (for edge caching)
 * - Strips authentication headers
 * - Adds cache-busting version parameter
 */
function createGetLink(uri: string): ApolloLink {
  return ApolloLink.from([
    new ApolloLink((operation, forward) => {
      // Build cache-busting URL parameters
      const urlParams = new URLSearchParams();

      // Add build time for automatic cache invalidation on each deployment
      const buildTime = process.env.BUILD_TIME || 'dev';

      urlParams.set('v', buildTime);

      // Append params to base URI
      const separator = uri.includes('?') ? '&' : '?';
      const fullUri = `${uri}${separator}${urlParams.toString()}`;

      operation.setContext({
        credentials: 'omit',
        headers: {
          ...GRAPHQL_BASE_HEADERS,
          // Explicitly remove Authorization if present
          Authorization: undefined,
        },
        uri: fullUri,
      });

      return forward(operation);
    }),
    new HttpLink({
      fetchOptions: {
        method: 'GET',
      },
    }),
  ]);
}

/**
 * Creates the POST link for regular queries and mutations
 * - Includes credentials
 * - Preserves authentication headers
 */
function createPostLink(uri: string): ApolloLink {
  return new HttpLink({
    uri,
    credentials: 'include',
    headers: {
      ...GRAPHQL_BASE_HEADERS,
    },
    fetchOptions: {
      method: 'POST',
    },
  });
}

/**
 * Creates the Apollo Link chain for the storefront client
 * Implements GET + APQ for allow-listed operations, POST for everything else
 */
export function createApolloLinks(uri: string, isSSR: boolean = false) {
  // SSR/Build clients should always use POST (no APQ, no GET)
  if (isSSR) {
    return new HttpLink({
      uri,
      credentials: 'include',
      headers: {
        ...GRAPHQL_SSR_HEADERS,
      },
    });
  }

  // Persisted queries link for client-side (with SHA-256)
  const persistedQueriesLink = createPersistedQueryLink({
    sha256,
    useGETForHashedQueries: false, // We handle GET routing ourselves via split
  });

  // Split traffic between GET (for APQ cached queries) and POST (everything else)
  const splitLink = split(
    shouldUseGet,
    createGetLink(uri), // true -> use GET link
    createPostLink(uri) // false -> use POST link
  );

  // Chain the links: persistedQueries -> split(GET/POST)
  return ApolloLink.from([persistedQueriesLink, splitLink]);
}
